import {
  Badge,
  Box,
  Button,
  Card,
  Message,
  Spinner,
  Stack
} from "@adfenix/alexandria";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  UseMutateAsyncFunction,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import {
  activateProgram,
  deactivateProgram,
  deleteProgram,
  duplicateProgram,
  getProgramDetails,
  updateProgram,
  requestActivation
} from "api/services/programService";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Divider from "components/Divider";
import { GenericError } from "components/GenericError";
import { HqIdRouteParams } from "components/SelectHqOfficeCombobox/helper";
import Page from "components/Page";
import PageHeader from "components/PageHeader";
import Popover from "components/Popover";
import { ProgramBundles } from "./bundle";
import { ProgramDetailsOverview } from "./Overview";
import { ProgramPriceSettings } from "./bundle/PriceSettings";
import { ProgramTriggers } from "./Triggers";
import { PublishingRules } from "./PublishingRules";
import { Switch } from "components/Switch/Switch";
import styled from "styled-components";
import { toast } from "react-toastify";
import { ProgramService } from "@adfenix/types";
import { useCheckPermission } from "hooks/usePermissions";
import { ProgramNotifications } from "./ProgramNotifications";
import { useOfficeAndHq } from "hooks/useOfficeAndHq";
import { PropertyTestOrderPageModal } from "./test-modals/PropertyTestOrderPageModal";
import { OfficeTestOrderPageModal } from "./test-modals/OfficeTestOrderPageModal";
import { AgentTestOrderPageModal } from "./test-modals/AgentTestOrderPageModal";
import { Link } from "react-router-dom";
import { HideProgram } from "./HideProgram";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { proccessAPIErrorResponse } from "utils/apiErrors";

const SaveBar = styled.div`
  position: fixed;
  bottom: 0px;
  width: calc(100vw - 320px);
  left: 320px;
  padding: 24px 0;
  background: #fff;
  box-shadow: 0px 0px 4px 0px #00000040;
  z-index: 2;

  // When the sidebar gets hidden
  @media (max-width: 770px) {
    width: 100vw;
    left: 0;
  }
`;
export type AppMode = "back-office" | "customer";

interface Props {
  appMode: AppMode;
}

const pageWidth = 1440;

export const ProgramBuilderDetails = ({ appMode }: Props) => {
  const { t } = useTranslation();

  const { programId } = useParams<{ programId: string }>();

  const [showTestOrderPageModal, setShowTestOrderPageModal] = useState(false);

  const navigate = useNavigate();
  const { hqId } = useParams<HqIdRouteParams>();
  const { hq } = useOfficeAndHq();

  const hasEditPermission = useCheckPermission({
    scope: "Program",
    action: "Edit"
  });

  const hasAdminEditPermission = useCheckPermission({
    scope: "ProgramAdmin",
    action: "Edit"
  });

  const hasAdminViewPermission = useCheckPermission({
    scope: "ProgramAdmin",
    action: "View"
  });

  const queryClient = useQueryClient();

  const {
    data,
    isInitialLoading: isLoading,
    isError
  } = useQuery(
    ["programDetails", programId],
    () => getProgramDetails(programId!),
    {
      enabled: Boolean(programId)
    }
  );

  const programInactive = data && !data.active;

  const {
    mutateAsync: triggerRequestActivation,
    isLoading: requestingActivation
  } = useMutation(requestActivation, {
    onSuccess: () => methods.setValue("requestedActivation", true)
  });

  const { mutateAsync: triggerActivateProgram, isLoading: activatingProgram } =
    useMutation(activateProgram);

  const {
    mutateAsync: triggerDeactivateProgram,
    isLoading: deActivatingProgram
  } = useMutation(deactivateProgram);

  const { mutateAsync: triggerUpdateProgram, isLoading: updatingProgram } =
    useMutation(updateProgram);

  const {
    mutateAsync: triggerDuplicateProgram,
    isLoading: duplicatingProgram
  } = useMutation(duplicateProgram);

  const { mutateAsync: triggerDeleteProgram, isLoading: deletingProgram } =
    useMutation(deleteProgram);

  const methods = useForm<Partial<ProgramService.ProgramModel>>({
    defaultValues: {
      ...data,
      bundleGroups: data?.bundleGroups?.sort(
        (a, b) => a.sortOrder - b.sortOrder
      )
    }
  });

  const { active, requestedActivation } = methods.watch();

  useEffect(() => {
    if (data) {
      methods.reset({
        ...data,
        bundleGroups: data?.bundleGroups?.sort(
          (a, b) => a.sortOrder - b.sortOrder
        )
      });
    }
  }, [data, methods]);

  if (isLoading)
    return (
      <Page>
        <Spinner />
      </Page>
    );

  if (isError || !data) return <GenericError />;

  return (
    <Page
      style={{ position: "relative", maxWidth: pageWidth }}
      fullWidth
      header={
        <Box
          pY="m"
          style={{ paddingTop: 0 }}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <PageHeader
            title={
              t("programBuilder.program.details.header") +
              data.name +
              " - " +
              (hq?.name ?? "")
            }
          />
        </Box>
      }
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(async values => {
            if (!programId) return false;

            try {
              const confirm = values.active
                ? window.confirm(
                    t(
                      "programBuilder.program.details.publishChnages.confirmation.message"
                    )
                  )
                : true; // No confirm if program is not active

              const program = removeTemporaryIds(values);
              if (confirm) {
                await triggerUpdateProgram({
                  program: {
                    ...program,
                    publicationDelayInHours: Number(
                      values.publicationDelayInHours
                    )
                  }
                });
                methods.reset(program);

                toast.success(
                  t(
                    "programBuilder.program.details.program.saving.toast.success"
                  )
                );

                queryClient.invalidateQueries(["programOverview"]);
                queryClient.invalidateQueries(["programDetails"]);
              }
            } catch (err) {
              const error = proccessAPIErrorResponse(
                err,
                t("programBuilder.program.details.program.saving.toast.error")
              );
              toast.error(error, {
                style: {
                  wordBreak: "break-all"
                },
                autoClose: 10000
              });
            }
          })}
        >
          {showTestOrderPageModal && data.subjectType === "Property" && (
            <PropertyTestOrderPageModal
              closeModal={() => setShowTestOrderPageModal(false)}
            />
          )}

          {showTestOrderPageModal && data.subjectType === "Office" && (
            <OfficeTestOrderPageModal
              closeModal={() => setShowTestOrderPageModal(false)}
            />
          )}

          {showTestOrderPageModal && data.subjectType === "Agent" && (
            <AgentTestOrderPageModal
              closeModal={() => setShowTestOrderPageModal(false)}
            />
          )}

          <Stack style={{ marginBottom: 72 }} spacing="xl2">
            {!programInactive && !hasAdminEditPermission && (
              <Message type="warning">
                {t(
                  "programBuilder.program.details.program.noPermission.message"
                )}
              </Message>
            )}
            <ProgramDetailsOverview />
            <ProgramTriggers />
            <PublishingRules />

            <ProgramNotifications />
            <HideProgram />
            <Card>
              <ProgramBundles />

              <Divider />

              <ProgramPriceSettings />
            </Card>
          </Stack>

          <SaveBar>
            <Controller
              render={() => (
                <>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    /* Prevent the save button from sliding off too far on wider screens */
                    style={{ maxWidth: pageWidth, padding: "0 56px" }}
                  >
                    <Box display="flex" alignItems="center">
                      {hasAdminEditPermission &&
                        (activatingProgram || deActivatingProgram ? (
                          <Spinner size={22} /> // Size 22 matches the size of the switch
                        ) : (
                          <Switch
                            disabled={!hasEditPermission}
                            onChange={async event => {
                              if (!programId) return;

                              if (event.currentTarget.checked) {
                                const activatedProgram =
                                  await handleActivateProgram(
                                    programId,
                                    triggerActivateProgram,
                                    t
                                  );

                                if (activatedProgram)
                                  methods.setValue("active", true);
                              } else {
                                const deactivatedProgram =
                                  await handleDeactivateProgram(
                                    programId,
                                    triggerDeactivateProgram,
                                    t
                                  );

                                if (deactivatedProgram)
                                  methods.setValue("active", false);
                              }

                              queryClient.invalidateQueries(["programDetails"]);
                            }}
                            checked={active}
                          />
                        ))}

                      <Box pX={"m"}>
                        {!(activatingProgram || deActivatingProgram) && (
                          <span>
                            {active
                              ? t(
                                  "programBuilder.program.details.program.active.label"
                                )
                              : t(
                                  "programBuilder.program.details.program.inactive.label"
                                )}
                          </span>
                        )}
                      </Box>

                      {!active &&
                        !requestedActivation &&
                        !hasAdminEditPermission && (
                          <Box pX={"m"}>
                            <Button
                              logLabel="reactivate-program-button"
                              disabled={methods.formState.isDirty}
                              variant="primary"
                              isLoading={requestingActivation}
                              onClick={async () => {
                                await triggerRequestActivation({
                                  programId: programId!
                                });
                              }}
                            >
                              {t(
                                "programBuilder.program.details.program.button.requestActivation"
                              )}
                            </Button>
                          </Box>
                        )}

                      {!active && requestedActivation && (
                        <Box pX={"m"}>
                          {t(
                            "programBuilder.program.details.program.requestedActivation.label"
                          )}
                        </Box>
                      )}

                      <Box>
                        <Button
                          logLabel="test-order-page-button"
                          disabled={methods.formState.isDirty}
                          variant="text"
                          onClick={() => setShowTestOrderPageModal(true)}
                        >
                          {t(
                            "programBuilder.program.details.program.button.testOrderPage"
                          )}
                        </Button>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center">
                      {methods.formState.isDirty && (
                        <Box pX="m">
                          <Badge color="warning">
                            {t(
                              "programBuilder.program.details.program.badge.unsaved"
                            )}
                          </Badge>
                        </Box>
                      )}
                      {hasAdminViewPermission && (
                        <Box pX="m">
                          <Link
                            to={`/back-office/hq/${hq?.id}/program-builder/${programId}/audit-log`}
                          >
                            {t(
                              "programBuilder.program.details.program.link.auditLog"
                            )}
                          </Link>
                        </Box>
                      )}
                      {hasAdminViewPermission && (
                        <Box pX="m">
                          <Link
                            to={`/back-office/hq/${hq?.id}/program-builder/${programId}/execution-log`}
                          >
                            Execution log
                          </Link>
                        </Box>
                      )}
                      {hasAdminViewPermission && (
                        <Box pX="m">
                          <Link
                            to={`/back-office/hq/${hq?.id}/program-builder/${programId}/monitoring`}
                          >
                            {t("programBuilder.program.monitoring.label")}
                          </Link>
                        </Box>
                      )}

                      <Button
                        logLabel="save-program-button"
                        isLoading={updatingProgram}
                        disabled={
                          updatingProgram ||
                          duplicatingProgram ||
                          deletingProgram ||
                          !methods.formState.isDirty ||
                          !hasEditPermission
                        }
                        type="submit"
                      >
                        {t(
                          "programBuilder.program.details.program.button.save"
                        )}
                      </Button>

                      <Popover
                        trigger={
                          <Button
                            logLabel="duplicate-program-button"
                            variant="text"
                            disabled={
                              updatingProgram ||
                              duplicatingProgram ||
                              deletingProgram ||
                              !hasEditPermission
                            }
                            isLoading={duplicatingProgram}
                            onClick={async () => {
                              const shouldDuplicate = window.confirm(
                                t(
                                  "programBuilder.program.details.program.duplicate.confirmation.message"
                                )
                              );

                              if (shouldDuplicate) {
                                try {
                                  const newProgramId =
                                    await triggerDuplicateProgram({
                                      programId: data.id,
                                      hqId: hqId!
                                    });

                                  toast.success(
                                    t(
                                      "programBuilder.program.details.program.duplicate.toast.success"
                                    )
                                  );
                                  navigate(
                                    `/${appMode}/hq/${hqId}/program-builder/${newProgramId}`
                                  );
                                } catch {
                                  toast.error(
                                    t(
                                      "programBuilder.program.details.program.duplicate.toast.error"
                                    )
                                  );
                                }
                              }
                            }}
                          >
                            <i className="uil uil-copy is-size-4" />
                          </Button>
                        }
                      >
                        {t(
                          "programBuilder.program.details.program.duplicate.popover"
                        )}
                      </Popover>

                      <Button
                        logLabel="delete-program-button"
                        variant="text"
                        isLoading={deletingProgram}
                        disabled={
                          updatingProgram ||
                          duplicatingProgram ||
                          deletingProgram ||
                          !hasAdminEditPermission
                        }
                        onClick={async () => {
                          const shouldRemove = window.confirm(
                            t(
                              "programBuilder.program.details.program.delete.confirmation.message",
                              { value: data.name }
                            ) +
                              t(
                                "programBuilder.program.details.program.delete.confirmation.message.part2"
                              )
                          );
                          if (shouldRemove) {
                            try {
                              await triggerDeleteProgram({
                                programId: data.id
                              });
                              toast.success(
                                t(
                                  "programBuilder.program.details.program.delete.toast.success"
                                )
                              );
                              navigate(
                                `/${appMode}/hq/${hqId}/program-builder`
                              );
                            } catch {
                              toast.error(
                                t(
                                  "programBuilder.program.details.program.delete.toast.error"
                                )
                              );
                            }
                          }
                        }}
                      >
                        <i className="uil uil-trash-alt is-size-4" />
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
              name="active"
            />
          </SaveBar>
        </form>
      </FormProvider>
    </Page>
  );
};

const handleDeactivateProgram = async (
  programId: string,
  triggerDeactivateProgram: UseMutateAsyncFunction<
    void,
    unknown,
    {
      programId: string;
    }
  >,
  t: TFunction
) => {
  try {
    const shouldDeactivate = window.confirm(
      t(
        "programBuilder.program.details.program.deactivate.confirmation.message"
      )
    );

    if (shouldDeactivate) {
      await triggerDeactivateProgram({ programId });
      return true;
    }
    return false;
  } catch (er) {
    if (typeof er === "string") toast.error(er);
    else
      toast.error(
        t("programBuilder.program.details.program.deactivate.toast.error")
      );
  }
};

const handleActivateProgram = async (
  programId: string,
  triggerActivateProgram: UseMutateAsyncFunction<
    void,
    unknown,
    {
      programId: string;
    }
  >,
  t: TFunction
) => {
  try {
    const shouldActivate = window.confirm(
      t("programBuilder.program.details.program.activate.confirmation.message")
    );

    if (shouldActivate) {
      await triggerActivateProgram({ programId });
      return true;
    }

    return false;
  } catch (er) {
    if (typeof er === "string") toast.error(er);
    else
      toast.error(
        t("programBuilder.program.details.program.activate.toast.error")
      );
  }
};

// Notifications in the bundle editing use a temporary id for sorting and identification.
// The back-end does not accept this ID because it's not a GUID and using real GUIDs for temporary IDs
// doesn't work because then the back-end will assume it's an existing notification and try to update it.
const removeTemporaryIds = (program: Partial<ProgramService.ProgramModel>) => {
  const identifier = /{temporary-id-(\d+)}/g;
  const stringifiedProgram = JSON.stringify(program, (_, value) => {
    if (typeof value === "string" && value.match(identifier)) {
      return null;
    }
    return value;
  });

  return JSON.parse(stringifiedProgram) as ProgramService.ProgramModel;
};
